<template>
  <el-dialog v-model="currentAttachment" width="80%">
    <div class="flex justify-center">
      <img v-if="isImage(currentAttachment?.filename)" class="el-upload-list__item-thumbnail"
           :src="settingsStore.storagePath + '/examinations/' + examination?.id + '/' + currentAttachment.filename"
           alt="" width="80%">
      <embed v-if="isPDF(currentAttachment?.filename)"
             :src="settingsStore.storagePath + '/examinations/' + examination?.id + '/' + currentAttachment.filename"
             width="100%" height="600px"/>
    </div>
  </el-dialog>

  <div class="flex flex-col gap-4">
    <ul class="flex flex-wrap el-upload-list el-upload-list--picture-card">

      <!-- Existing saved attachments (after save) -->
      <li v-for="(attachment, i) in examination?.examinationAttachments" :key="i"
          class="el-upload-list__item">
        <div class="w-[148px]">
          <template v-if="attachment.mimetype === 'image/png' || attachment.mimetype === 'image/jpeg'">
            <img class="el-upload-list__item-thumbnail"
                 :src="settingsStore.storagePath + '/examinations/' + examination?.id + '/' + attachment.filename"
                 alt="">
          </template>
          <template v-else>
            <div class="w-full h-full flex items-center justify-center">
              <el-icon style="font-size: 48px">
                <Document/>
              </el-icon>
            </div>
          </template>

          <label class="absolute left-[50%] translate-x-[-50%] bottom-0 bg-black/45 text-white p-1 w-[100%] truncate">
            {{ attachment.originalFilename }}
          </label>

          <span class="el-upload-list__item-actions">
            <span v-if="canBePreviewed(attachment.filename)" class="el-upload-list__item-preview">
              <el-icon @click="() => { currentAttachment = attachment }">
                <Icon icon="icon-park-outline:preview-open"/>
              </el-icon>
            </span>
            <span class="el-upload-list__item-preview">
              <el-icon @click="() => { downloadAttachment(attachment) }">
                <Download/>
              </el-icon>
            </span>
            <span class="el-upload-list__item-delete">
              <el-icon @click="() => { handleRemoveOnRemote(attachment) }">
                <Delete/>
              </el-icon>
            </span>
          </span>
        </div>
      </li>

      <!-- Staged (pending) attachments — shown before save -->
      <li v-for="(file, i) in props.modelValue" :key="'pending-' + i"
          class="el-upload-list__item opacity-60">
        <div class="w-[148px]">
          <template v-if="file.type === 'image/png' || file.type === 'image/jpeg'">
            <img class="el-upload-list__item-thumbnail"
                 :src="getStagedPreviewUrl(file)" alt="">
          </template>
          <template v-else>
            <div class="w-full h-full flex items-center justify-center">
              <el-icon style="font-size: 48px">
                <Document/>
              </el-icon>
            </div>
          </template>

          <label class="absolute left-[50%] translate-x-[-50%] bottom-0 bg-black/45 text-white p-1 w-[100%] truncate">
            {{ file.name }}
          </label>

          <span class="el-upload-list__item-actions">
            <span class="el-upload-list__item-delete">
              <el-icon @click="() => { removeStagedFile(i) }">
                <Delete/>
              </el-icon>
            </span>
          </span>
        </div>
      </li>

      <!-- Upload button -->
      <li>
        <el-upload
            class="w-[146px] h-[147px]"
            drag
            multiple
            :show-file-list="false"
            :action="examination?.id ? `${Examination.ENDPOINT}/${examination.id}/attachments` : ''"
            :on-success="onUploadSuccess"
            :on-error="showError"
            :http-request="handleUpload"
        >
          <div class="m-[17px] text-xl">
            <el-icon>
              <Plus/>
            </el-icon>
          </div>
        </el-upload>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { Delete, Document, Download, Plus } from '@element-plus/icons-vue'
import type { UploadFile, UploadFiles, UploadRequestOptions } from 'element-plus'
import { ElMessage } from 'element-plus'
import { useHTTP } from '@/plugins/http'
import { useSettingsStore } from '@/stores/settingsStore'
import Examination, { ExaminationAttachment } from '@/models/customer/Examination'
import { Icon } from '@iconify/vue'
import { canBePreviewed, isImage, isPDF } from '@/lib/utils'

const props = defineProps<{
  data: Examination
  modelValue: File[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', files: File[]): void
}>()

const http = useHTTP()
const settingsStore = useSettingsStore()
const examination = ref<Examination>()
const currentAttachment = ref<ExaminationAttachment>()
const stagedPreviewUrls = ref<Map<File, string>>(new Map())

onMounted(() => {
  examination.value = props.data
})

watch(() => props.data, (newVal) => {
  examination.value = newVal
}, { deep: true })

watch(() => props.modelValue, (newFiles, oldFiles) => {
  // Revoke URLs for removed files
  if (oldFiles) {
    for (const f of oldFiles) {
      if (!newFiles.includes(f)) {
        const url = stagedPreviewUrls.value.get(f)
        if (url) { URL.revokeObjectURL(url); stagedPreviewUrls.value.delete(f) }
      }
    }
  }
  // Create URLs for new files
  for (const f of newFiles) {
    if (!stagedPreviewUrls.value.has(f)) {
      stagedPreviewUrls.value.set(f, URL.createObjectURL(f))
    }
  }
})

function getStagedPreviewUrl(file: File): string {
  return stagedPreviewUrls.value.get(file) ?? ''
}

function removeStagedFile(index: number) {
  const updated = [...props.modelValue]
  updated.splice(index, 1)
  emit('update:modelValue', updated)
}

function handleUpload(options: UploadRequestOptions) {
  if (!examination.value?.id) {
    // Stage locally before save
    emit('update:modelValue', [...props.modelValue, options.file as File])
    return Promise.resolve()
  }
  const data = new FormData()
  data.set('attachment', options.file)
  return http.put(options.action, data)
}

function handleRemoveOnRemote(attachment: ExaminationAttachment) {
  http.delete(`${Examination.ENDPOINT}/attachments/${attachment.id}`).then(() => {
    onUploadSuccess()
  })
}

async function downloadAttachment(attachment: ExaminationAttachment) {
  try {
    const response = await fetch(
      settingsStore.storagePath + '/examinations/' + examination.value?.id + '/' + attachment.filename
    )
    if (!response.ok) throw new Error('Network response was not ok')
    const blob = await response.blob()
    const urlObject = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = urlObject
    link.download = attachment.originalFilename || 'downloaded-file'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(urlObject)
  } catch (error) {
    console.error('Error downloading the file:', error)
  }
}

function showError(error: any, _uploadFile: UploadFile, _uploadFiles: UploadFiles) {
  ElMessage({ showClose: true, message: error?.title, type: 'error' })
}

async function onUploadSuccess() {
  if (!examination.value?.id) return
  examination.value = await http.get<Examination>(Examination.ENDPOINT + `/${examination.value?.id}`)
}
</script>
